import createSvpAccess from '@vgtv/svp-access';
import UserSettings from '@vg/user-settings';
import { ProviderEnvMap } from '@vgtv/svp-access/lib/svp_access';
import { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type {
  LoginOptions,
  Identity,
  HasSessionSuccessResponse,
  HasSessionFailureResponse,
} from '@schibsted/account-sdk-browser/src/identity';
import type { Monetization } from '@schibsted/account-sdk-browser/src/monetization';

import { resolveWithTimeout } from '../../utils/async';
import type { StreamProvider } from '@vgtv/api-client';

const DEFAULT_TIMEOUT_IN_MILLISECONDS = 4000;

// --- Event-based snapshot store ---

type Listener = () => void;
const listeners = new Set<Listener>();

export interface IdentitySnapshot {
  userId: number | null;
  username: string | null;
  uuid: string | null;
  userAccesses: ProviderAccessMap[StreamProvider][];
  location: { eea?: boolean; local?: boolean };
  /**
   * True once the initialization sequence completes — either because real data
   * arrived or because the timeout elapsed and we fell back to the default state.
   */
  initialized: boolean;
  /**
   * True only when real identity data arrived (not a timeout-fallback state).
   * Consumers can use this to react when late-resolving data finally comes in.
   */
  resolved: boolean;
}

let snapshot: IdentitySnapshot = {
  userId: null,
  username: null,
  uuid: null,
  userAccesses: [],
  location: {},
  initialized: false,
  resolved: false,
};

/**
 * Subscribe to snapshot changes. Returns an unsubscribe function.
 * Compatible with Preact/React `useSyncExternalStore` and plain `useEffect`.
 */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSnapshot(): IdentitySnapshot {
  return snapshot;
}

function notify() {
  // Create a new reference so shallow-equality checks detect the change.
  snapshot = { ...snapshot };
  for (const listener of listeners) listener();
}

/**
 * Returns a promise that resolves once `snapshot.initialized` becomes true
 * (i.e. real data arrived OR the timeout fired). Resolves immediately if
 * already initialized.
 */
export function waitForInitialization(
  timeout = DEFAULT_TIMEOUT_IN_MILLISECONDS
): Promise<IdentitySnapshot> {
  if (snapshot.initialized) {
    return Promise.resolve(snapshot);
  }

  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsub();
      // Mark as initialized even if no data arrived — callers should fall back.
      if (!snapshot.initialized) {
        snapshot = { ...snapshot, initialized: true };
        notify();
      }
      resolve(snapshot);
    }, timeout);

    const unsub = subscribe(() => {
      if (snapshot.initialized) {
        clearTimeout(timer);
        unsub();
        resolve(snapshot);
      }
    });
  });
}

// --- Module-level state (kept for backward-compatible sync getters) ---

function memoizePromise<T>(fn: () => Promise<T>) {
  let called = false;
  let value: T;
  return async () => {
    if (!called) {
      try {
        value = await fn();
        called = true;
      } catch {
        // noop
      }
    }
    return value;
  };
}

let identityInstancePromise: Promise<Identity>;
let monetizationInstancePromise: Promise<Monetization>;
let userId: number | null = null;
let username: string;
let uuid: string | undefined;
let userDataPromise: Promise<void>;
let userAccesses: ProviderAccessMap[StreamProvider][];
let userAccessesGetter: () => Promise<void>;
let provider: StreamProvider;
let schibstedAccountEnv: ProviderEnvMap[StreamProvider];
let svpAccess: ReturnType<typeof createSvpAccess>;

let locationPromise: Promise<void>;
let location: {
  eea?: boolean;
  local?: boolean;
} = {};

async function getSessionWithTimeout(
  timeout = DEFAULT_TIMEOUT_IN_MILLISECONDS
) {
  const session = await resolveWithTimeout({
    promise: identityInstancePromise.then((identity) => identity.hasSession()),
    fallback: undefined,
    timeout,
  });

  if (!session) {
    return {
      error: {
        code: 0,
        description: `Timeout while retrieving session data after ${timeout}ms`,
        type: 'timeout',
      },
      response: {} as any,
    } as HasSessionFailureResponse;
  }

  return session;
}

async function setUserData() {
  try {
    const identity = await identityInstancePromise;
    const session = await identity.hasSession();
    if ('error' in session) {
      throw new Error(session.error.description);
    }

    userId = session.userId;
    username = session.givenName;
    uuid = session.uuid;

    snapshot.userId = userId;
    snapshot.username = username;
    snapshot.uuid = uuid ?? null;
    snapshot.resolved = true;
    notify();
  } catch (err) {
    console.error(err);
  }
}

async function setLocation() {
  try {
    location = await svpAccess.checkLocation();

    snapshot.location = location;
    notify();
  } catch (err) {
    console.error(err);
  }
}

async function setUserAccesses() {
  try {
    userAccesses = await svpAccess.getAccesses();

    snapshot.userAccesses = [...userAccesses];
    notify();
  } catch (err) {
    console.error(err);
  }
}

export async function initIdentity(
  getIdentityInstance?: () => Promise<Identity>,
  forceReload?: boolean
): Promise<Identity> {
  if (identityInstancePromise && !forceReload) {
    return identityInstancePromise;
  }

  identityInstancePromise = new Promise((resolve) => {
    if (typeof getIdentityInstance === 'function') {
      getIdentityInstance().then((instance) => resolve(instance));
    }
  });

  if (typeof getIdentityInstance === 'function') {
    userDataPromise = resolveWithTimeout({
      promise: setUserData(),
      fallback: undefined,
      timeout: DEFAULT_TIMEOUT_IN_MILLISECONDS,
    });

    // Mark the snapshot as initialized once the timeout window has closed,
    // regardless of whether real data arrived. setUserData() will continue
    // running in the background and will call notify() when/if it resolves.
    userDataPromise.then(() => {
      if (!snapshot.initialized) {
        snapshot = { ...snapshot, initialized: true };
        notify();
      }
    });
  }

  return identityInstancePromise;
}

export async function initMonetization(
  getMonetizationInstance: undefined | (() => Promise<Monetization>),
  vendor: StreamProvider,
  env: ProviderEnvMap[StreamProvider],
  forceReload?: boolean
) {
  if (monetizationInstancePromise && !forceReload) {
    return monetizationInstancePromise;
  }

  monetizationInstancePromise = new Promise((resolve) => {
    if (typeof getMonetizationInstance === 'function') {
      getMonetizationInstance().then((instance) => resolve(instance));
    }
  });

  provider = vendor;
  schibstedAccountEnv = env;

  svpAccess = createSvpAccess({
    provider,
    env: schibstedAccountEnv,
    getIdentityInstance: () => identityInstancePromise,
  });

  locationPromise = setLocation();

  if (typeof getMonetizationInstance === 'function') {
    userAccessesGetter = memoizePromise(() =>
      resolveWithTimeout({
        promise: setUserAccesses(),
        timeout: 2500,
        fallback: undefined,
      })
    );
  }

  return monetizationInstancePromise;
}

export async function login(props: Partial<LoginOptions> = {}) {
  const identity = await identityInstancePromise;

  await identity.login({ state: Date.now().toString(), ...props });

  await Promise.all([setUserData(), setUserAccesses()]);
}

export async function clearSessionCache() {
  const identity = await identityInstancePromise;

  identity.clearCachedUserSession();
}

export function getIdentity() {
  return identityInstancePromise;
}

export async function clearAccessCache() {
  try {
    await svpAccess.clearAccessCache();
  } catch (err) {
    console.error(err);
  }
}

export async function getSessionCookie() {
  try {
    const session = await getSessionWithTimeout();
    if ('error' in session) {
      throw new Error(session.error.description);
    }

    return session.sp_id;
  } catch (error) {
    return null;
  }
}

export async function getUserSettings() {
  const session = await getSessionWithTimeout();
  if ('error' in session) {
    throw new Error(session.error.description);
  }

  return new UserSettings(session as HasSessionSuccessResponse);
}

export async function getSessionSignature() {
  try {
    const session = await getSessionWithTimeout();
    if ('error' in session) {
      throw new Error(session.error.description);
    }

    return session.sig;
  } catch (error) {
    return null;
  }
}

export function getUserId() {
  return userId;
}

export async function getUserIdAsync() {
  // make first username getter blocking
  await userDataPromise;

  return userId;
}

export async function getUserUuidAsync() {
  // make first uuid getter blocking
  await userDataPromise;

  return uuid;
}

export async function getUsername() {
  // make first username getter blocking
  await userDataPromise;

  return username;
}

export async function getUserAccesses<T extends StreamProvider>() {
  // make first user accesses getter blocking
  await userAccessesGetter();

  return (userAccesses ?? []) as ProviderAccessMap[T][];
}

export async function getLocation() {
  await locationPromise;
  return location;
}
